// src/features/links/highlight.tsx
// IR-1233 r3 — shared search-match highlighter for the Join Links table.
// Wraps every case-insensitive occurrence of the active search query in a
// styled <mark data-search-match>. The attribute is load-bearing: the
// Ctrl+F-style match navigation (useMatchNav) discovers matches by querying
// `[data-search-match]` in document order, and flips `data-active` on the
// current one (the data-[active=true]: variants below restyle it).
import type React from "react";

/** Escape regex metacharacters so the query is matched literally. */
function escapeRegExp(q: string): string {
  return q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export interface SnippetOptions {
  /** Roughly how many characters of the value the cell actually shows. */
  visibleWindow: number;
  /** Characters of context to keep before the match when re-centering. */
  leadIn: number;
}

/**
 * IR-1233 r6 — re-center a clamped/truncated value on the query's first
 * occurrence so the `<mark>` lands inside the cell's visible window.
 *
 * A mark that exists in the DOM but sits past a `truncate` / `line-clamp`
 * boundary is worse than no mark at all: `useMatchNav` counts it and scrolls to
 * it, and the analyst sees nothing highlighted. Extracted from the Bio cell's
 * r4 `bioSnippet` so the Link cell (max-w-[28ch] truncate) can use the same
 * rule — the invariant is that every backend-matchable string must be
 * *visibly* rendered, not merely rendered.
 *
 * Returns `text` unchanged when `q` is empty/absent, never occurs, or already
 * starts inside `visibleWindow`. Otherwise returns an ellipsis-prefixed
 * snippet starting `leadIn` characters before the occurrence, snapped forward
 * to the nearest whitespace (avoid mid-word starts; a no-op for URLs).
 */
export function snippetAroundMatch(
  text: string,
  q: string | null | undefined,
  { visibleWindow, leadIn }: SnippetOptions,
): string {
  const needle = q?.trim();
  if (!needle) return text;
  const idx = text.toLowerCase().indexOf(needle.toLowerCase());
  if (idx < 0 || idx < visibleWindow) return text;
  // Clamp at 0: with a small visibleWindow (the Link cell) `idx - leadIn` can
  // go negative, and String.slice(-n) silently returns the TAIL of the string —
  // i.e. a completely different value rendered as if it were the match context.
  let start = Math.max(0, idx - leadIn);
  const lead = text.slice(start, idx);
  const ws = lead.search(/\s/);
  if (ws >= 0) start += ws + 1;
  return `…${text.slice(start)}`;
}

/**
 * Highlight every case-insensitive occurrence of `q` inside `text` with a
 * `<mark data-search-match>`. Returns `text` unchanged when `q` is empty /
 * whitespace-only or never occurs — callers can wire it unconditionally.
 * The original casing of matched segments is preserved (only wrapped).
 */
export function highlightMatches(text: string, q: string | null | undefined): React.ReactNode {
  const needle = q?.trim();
  if (!needle) return text;
  const parts = text.split(new RegExp(`(${escapeRegExp(needle)})`, "gi"));
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <mark
        // biome-ignore lint/suspicious/noArrayIndexKey: split segments are positional and static per render.
        key={i}
        data-search-match
        // IR-1233 r4: OPAQUE yellow + dark text — the previous 30% translucent
        // color-mix background left inactive marks near-invisible on the dark
        // theme. The active mark stays orange (distinct from the yellow rest).
        className="rounded-sm bg-[var(--iris-neon-yellow)] text-[var(--iris-bg-1)] data-[active=true]:bg-[var(--iris-neon-orange)] data-[active=true]:text-[var(--iris-bg-1)]"
      >
        {part}
      </mark>
    ) : (
      part
    ),
  );
}
